/* eslint-disable import/order */
import useMainMenuTranslationsFromInitialState from './hooks/useMainMenuTranslationsFromInitialState';
import type { MainMenuWrapperProps } from './types';
import { getSourceEventParams } from '@crehana/analytics';
import {
  NOTIFICATIONS_FETCH,
  useGetNotification,
} from '@crehana/compass.notification';
import { mergeTranslations, useTranslation } from '@crehana/i18n';
import { useDisclosure } from '@crehana/react-hooks';
import { MainMenu, MainMenuSubmenu, TopBanner, useMe } from '@crehana/web';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';
import { useAuthDialog } from 'Jsx/ContextApis/AuthDialog';
import { useUITheme } from 'Jsx/ContextApis/UITheme';
import CookiesBanner from 'Jsx/CrehanaWeb/CookiesBanner';
import DialogGetPhone from 'Jsx/Shared/DialogGetPhone';
import useFeatureFlagQuery from 'Jsx/Shared/Hooks/useFeatureFlagQuery/useFeatureFlagQuery';
import { ContactSourceEnum } from 'Jsx/Types/Graphql/globals';
import getFeatureFlag from 'Utils/getFeatureFlag';
import React, { FC, Fragment, useState } from 'react';
import useHigherEducationHomeUserUI from 'Jsx/Classroom/CourseDashboard/hooks/useHigherEducationHomeUserUI';

const MainMenuWrapper: FC<MainMenuWrapperProps> = ({
  countryPrefix: countryPrefixProp,
  customLocales: customLocalesProp,
  isAnonymous,
  disableAuthDialog,
  topBannerProps,
  customTopBanner,
  ...mainMenuProps
}) => {
  const shoppingCartData = useProductsContainer();
  const { theme, toggleTheme } = useUITheme();
  const { showLoginModal, showRegisterModal, preloadAuthDialog } =
    useAuthDialog();

  const { isActive: showNotification } = useFeatureFlagQuery({
    flagKey: 'user_notification_web',
  });
  const hideMarketplaceUIFF = useHigherEducationHomeUserUI();
  const { me } = useMe();

  const {
    notificationData,
    error,
    hasMore,
    loading: loadingNotification,
    onFetchMore,
    totalCount,
  } = useGetNotification({
    first: NOTIFICATIONS_FETCH,
    isSkip: !showNotification,
  });

  const { languageCode } = useTranslation();
  const customLocales = useMainMenuTranslationsFromInitialState();
  const isAllAccessEnabled = getFeatureFlag('all_access_marathon');
  const isFullPassMembership = false;

  //HE validations
  const isMarketplaceUserHEStudent = me?.organization?.myRole === 'high-education-member' && hideMarketplaceUIFF;

  const onLogin = () => {
    const nextUrl = window.location.pathname + window.location.search;

    if (isAllAccessEnabled) {
      window.location.href = `/entrar/?next=${nextUrl}`;
      return;
    }

    showLoginModal(nextUrl);
  };

  const onRegister = () => {
    const nextUrl = window.location.pathname + window.location.search;

    if (isAllAccessEnabled) {
      window.location.href = `/registro/?next=${nextUrl}`;
      return;
    }

    showRegisterModal(
      nextUrl,
      undefined,
      {
        isRenderedIn: mainMenuProps.amplitudePageName,
        ...getSourceEventParams(),
      },
      'Main Menu',
    );
  };

  const phoneLeadDialogState = useDisclosure(false);
  const [topBannerIsOpen, setTopBannerIsOpen] = useState(false);
  const [hideTopBanner, setHideTopBanner] = useState<boolean>(false);

  const onToggleTopBanner = (isOpen: boolean) => {
    setTopBannerIsOpen(isOpen);
  };

  const authProps = !disableAuthDialog
    ? { onLogin, onRegister, onAuthButtonHover: preloadAuthDialog }
    : {};

  const canRenderTopBanner = !mainMenuProps.layoutConfig?.hideTopBanner;
  const canRenderCookiesBanner = !mainMenuProps.layoutConfig?.hideCookiesBanner;

  const mainMenuLocales = React.useMemo(() => {
    if (customLocalesProp) {
      return mergeTranslations(customLocales, customLocalesProp);
    }
    return customLocales;
  }, [customLocales, customLocalesProp]);

  const onChangeActiveSubmenu = (activeSubmenu?: MainMenuSubmenu) => {
    setHideTopBanner(activeSubmenu === 'search');
  };

  const MainMenuComponent = (
    <MainMenu
      {...mainMenuProps}
      userTheme={theme}
      onToggleUserTheme={toggleTheme}
      countryPrefix={countryPrefixProp || languageCode || ''}
      customLocales={mainMenuLocales}
      topBannerIsOpen={topBannerIsOpen}
      shoppingCartData={shoppingCartData}
      onOpenPhoneDialog={phoneLeadDialogState.onOpen}
      onChangeActiveSubmenu={onChangeActiveSubmenu}
      notification={{
        notificationData,
        error,
        hasMore,
        loading: loadingNotification,
        onFetchMore,
        totalCount,
        totalUnRead: notificationData.totalUnRead,
      }}
      layoutConfig={{
        ...mainMenuProps.layoutConfig,
        hideNotifications: !showNotification || isMarketplaceUserHEStudent,
      }}
      {...authProps}
    />
  );

  return (
    <Fragment>
      {/* MainMenu and Top Banner */}

      {customTopBanner ? (
        <header className="relative">
          {customTopBanner}
          {MainMenuComponent}
        </header>
      ) : (
        <Fragment>
          {canRenderTopBanner && !hideTopBanner && (
            <TopBanner
              {...topBannerProps}
              amplitudePageName={mainMenuProps.amplitudePageName}
              onToggleTopBanner={onToggleTopBanner}
              isAllAccessEnabled={isAllAccessEnabled}
              isFullPassMembership={isFullPassMembership}
            />
          )}
          {canRenderTopBanner && mainMenuProps.position === 'absolute' ? (
            <header className="relative">{MainMenuComponent}</header>
          ) : (
            MainMenuComponent
          )}
        </Fragment>
      )}

      {/* Additional components */}

      {/* Cookies Banner */}
      {canRenderCookiesBanner && <CookiesBanner />}

      {/* Call Center Dialog */}
      {phoneLeadDialogState.isOpen && (
        <DialogGetPhone
          hidenDialog={phoneLeadDialogState.onClose}
          sourceRender={ContactSourceEnum.SOURCE_SEARCH_POP_UP}
        />
      )}
    </Fragment>
  );
};

export default React.memo(MainMenuWrapper);
